'use client';

import {
  BottomTabBarHeightSetterContext,
  cn,
  Div,
  DivProps,
} from '@scatterlab/zeta-core';
import {
  createElement,
  ElementType,
  memo,
  use,
  useEffect,
  useRef,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import NavigationBarItem, { NavigationBarItemProps } from './NavigationBarItem';

export type NavigationBarProps<ItemComponent extends ElementType = typeof Div> =
  {
    items: NavigationBarItemProps<ItemComponent>[];
  } & DivProps;

const NavigationBar = <ItemComponent extends ElementType = typeof Div>({
  items,
  className,
  ref,
  ...props
}: NavigationBarProps<ItemComponent>) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const setBottomTabBarHeight = use(BottomTabBarHeightSetterContext);

  useEffect(() => {
    if (!setBottomTabBarHeight) return;
    setBottomTabBarHeight(innerRef.current?.clientHeight ?? 0);
  }, [setBottomTabBarHeight]);

  return (
    <Div
      className={cn(
        'flex h-[52px] shrink-0 flex-row items-center justify-between rounded-t-xl bg-gray-main px-[28px] pt-0.5',
        className,
      )}
      {...props}
      ref={mergeRefs([ref, innerRef])}
    >
      {items.map((item) =>
        createElement(NavigationBarItem, { ...item, key: item.id }),
      )}
    </Div>
  );
};

export default memo(NavigationBar) as typeof NavigationBar;
